import { Layout } from "antd";
import _ from "lodash";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { ReactComponent as ICON_CAMPAIGNS } from "@assets/svgs/ICON_CAMPAIGNS.svg";
import { ReactComponent as ICON_DASHBOARD } from "@assets/svgs/ICON_DASHBOARD.svg";
import { ReactComponent as ICON_DEVICE_MANAGEMENT } from "@assets/svgs/ICON_DEVICE_MANAGEMENT.svg";
import { ReactComponent as ICON_GEAR_SETTING } from "@assets/svgs/ICON_GEAR_SETTING.svg";
import { ReactComponent as ICON_MEDIAS } from "@assets/svgs/ICON_MEDIAS.svg";
import { ReactComponent as ICON_MENU_ARROW } from "@assets/svgs/ICON_MENU_ARROW.svg";
import { ReactComponent as ICON_REPORTS } from "@assets/svgs/ICON_REPORTS.svg";
import { ReactComponent as ICON_USERS_MANAGEMENTS } from "@assets/svgs/ICON_USERS_MANAGEMENTS.svg";
import { ReactComponent as LOGO_PAGE } from "@assets/svgs/LOGO_PAGE.svg";
import { Icon } from "@components/Icon";
import { Text } from "@components/Text";
import { COLOR_WHITE } from "@static/COLORS";
import { MAPPING_LOCATION_TO_MENU_KEY } from "@static/MENU_LIST";
import {
  KEY_CAMPAIGNS,
  KEY_DASHBOARD,
  KEY_DEVICE_MANAGEMENT,
  KEY_IMPORTANT,
  KEY_MANAGEMENT,
  KEY_MEDIAS,
  KEY_REPORTS,
  KEY_USERS_MANAGEMENTS,
} from "@static/PERMISSION_KEY";
import { MENU_SIZE } from "@static/SIZE";

const { Sider } = Layout;

const SectionLogo = styled.div`
  height: 104px;
  width: 100%;
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

// #5E6366

const MenuContainer = styled.div`
  width: ${MENU_SIZE}px;
`;

const SectionGroupMenu = styled.div`
  display: flex;
  cursor: pointer;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 53px;
  padding: 16px;
  background: ${({ isSelected = false }) =>
    isSelected ? "rgba(0, 0, 0, 0.10)" : "transparent"};
`;

const SectionGroupLabel = styled.div`
  display: flex;
`;

const GroupMenuTitle = styled(Text)`
  cursor: pointer;
  font-weight: 600;
  line-height: 21px;
  background: ${({ isSelected = false }) =>
    isSelected
      ? "linear-gradient(270deg, #0eb8b8 0%, #1cdbab 100%)"
      : "rgba(255, 255, 255, 0.4)"};

  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SectionSubMenu = styled.div`
  cursor: pointer;
  padding-left: 48px;
  display: flex;
  align-items: center;
  height: 45px;
  width: 100%;
  background: ${({ isSelected = false }) =>
    isSelected
      ? "linear-gradient(270deg, #0EB8B8 0%, #1CDBAB 100%)"
      : "transparent"};
`;

const SubMenuRectangle = styled.div`
  width: 3px;
  height: 16px;
  margin-right: 8px;
  background: ${({ isSelected = false }) =>
    isSelected ? COLOR_WHITE : "transparent"};
`;

const SubMenuTitle = styled(Text)`
  font-weight: 400;
  line-height: 21px;
  color: ${COLOR_WHITE};
`;

const INCLUDE_SUB_PATH = [
  "/devices-list",
  "/devices-prediction",
  "/device-groups-list",
];

const ACTIVE_SUB_PATH = {
  "devices-list": "/devices",
  "devices-prediction": "/devices",
  "device-groups-list": "/device-groups",
  prediction: "/devices",
};

const resolveBasePath = (pathname) => {
  const parts = _.chain(pathname).split("/").filter(Boolean).value();
  if (parts?.length > 1) {
    return `${parts[0]}`;
  }
  return parts;
};

export const SideMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = _.get(location, ["pathname"]);

  const menuList = useSelector(({ important }) => {
    return _.get(important, ["data", "initialMenu"]);
  });

  const basePath = resolveBasePath(pathname);
  // const replacePathname = _.chain(pathname).split("/").value();

  const [selectGroupMenuKey, setSelectGroupMenu] = useState(
    _.get(MAPPING_LOCATION_TO_MENU_KEY, [basePath])
  );

  const onHandleMenu = ({
    handleFormChildren = false,
    menuKey = null,
    location,
  }) => {
    const updateIsOpenSubMenu = handleFormChildren
      ? menuKey
      : selectGroupMenuKey === menuKey
      ? null
      : menuKey;

    setSelectGroupMenu(updateIsOpenSubMenu);
    if (!_.isNil(location)) {
      navigate(location);
    }
  };

  return (
    <Sider
      width={240}
      style={{
        overflow: "auto",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        border: "none",
      }}
    >
      <SectionLogo>
        <Icon width={"164px"} height={"32px"}>
          <LOGO_PAGE />
        </Icon>
      </SectionLogo>
      <MenuContainer>
        {_.map(menuList, (item) => {
          const menuKey = _.get(item, ["key"]);
          const menuLabel = _.get(item, ["label"]);
          const menuLocation = _.get(item, ["location"]);
          const isChildren = _.get(item, ["isChildren"]);
          const menuChildren = _.get(item, ["children"]);
          const fillColor =
            menuKey === _.get(MAPPING_LOCATION_TO_MENU_KEY, [basePath])
              ? "#1CDBAB"
              : "rgba(255, 255, 255, 0.4)";
          return (
            <div key={menuKey}>
              <SectionGroupMenu
                key={menuKey}
                isSelected={menuKey === selectGroupMenuKey}
                onClick={() =>
                  onHandleMenu({
                    handleFormChildren: false,
                    isChildren,
                    menuKey,
                    location: menuLocation,
                  })
                }
              >
                <SectionGroupLabel>
                  <Icon style={{ marginRight: 8 }} isCursor>
                    {(() => {
                      switch (menuKey) {
                        case KEY_DASHBOARD:
                          return <ICON_DASHBOARD fill={fillColor} />;
                        case KEY_DEVICE_MANAGEMENT:
                          return <ICON_DEVICE_MANAGEMENT fill={fillColor} />;
                        case KEY_IMPORTANT:
                          return <ICON_CAMPAIGNS fill={fillColor} />;
                        case KEY_CAMPAIGNS:
                          return <ICON_CAMPAIGNS fill={fillColor} />;
                        case KEY_MEDIAS:
                          return <ICON_MEDIAS fill={fillColor} />;
                        case KEY_REPORTS:
                          return <ICON_REPORTS fill={fillColor} />;
                        case KEY_USERS_MANAGEMENTS:
                          return <ICON_USERS_MANAGEMENTS fill={fillColor} />;
                        case KEY_MANAGEMENT:
                          return <ICON_GEAR_SETTING fill={fillColor} />;

                        default:
                          return null;
                      }
                    })()}
                  </Icon>
                  <GroupMenuTitle
                    isSelected={
                      menuKey ===
                      _.get(MAPPING_LOCATION_TO_MENU_KEY, [basePath])
                    }
                  >
                    {menuLabel}
                  </GroupMenuTitle>
                </SectionGroupLabel>
                {isChildren && (
                  <Icon
                    isCursor
                    width={"16px"}
                    height={"16px"}
                    fill={fillColor}
                    isRotated={menuKey === selectGroupMenuKey}
                  >
                    <ICON_MENU_ARROW />
                  </Icon>
                )}
              </SectionGroupMenu>
              {selectGroupMenuKey === menuKey &&
                _.map(menuChildren, (child) => {
                  const childMenuKey = _.get(child, ["key"]);
                  const childMenuLabel = _.get(child, ["label"]);
                  const childMenuLocation = _.get(child, ["location"]);
                  const mappingPathname = _.chain(pathname)
                    .thru((item) => {
                      console.log("item :>> ", item);
                      const basePath = resolveBasePath(item);
                      console.log("basePath :>> ", basePath);
                      const result = _.get(ACTIVE_SUB_PATH, [basePath], item);
                      return result;
                    })
                    .value();
                  return (
                    <SectionSubMenu
                      key={childMenuKey}
                      isSelected={mappingPathname === childMenuLocation}
                      onClick={() =>
                        onHandleMenu({
                          handleFormChildren: true,
                          menuKey,
                          location: childMenuLocation,
                        })
                      }
                    >
                      <SubMenuRectangle
                        isSelected={mappingPathname === childMenuLocation}
                      />
                      <SubMenuTitle>{childMenuLabel}</SubMenuTitle>
                    </SectionSubMenu>
                  );
                })}
            </div>
          );
        })}
      </MenuContainer>
    </Sider>
  );
};
